import { NavLink } from 'react-router-dom';
import { LuPanelLeftClose, LuPanelLeftOpen, LuX } from 'react-icons/lu';
import { NAV_GROUP_LABELS, NAV_ITEMS, type NavItem } from '@/app/navigation';
import { useAuth } from '@/features/auth/AuthProvider';
import { APP_SHORT_NAME } from '@/config/env';
import { cn } from '@/lib/cn';
import { AppLogo } from './AppLogo';

function Logo({ collapsed }: { collapsed: boolean }) {
  return (
    <div className="flex items-center gap-2.5">
      <AppLogo className="size-9" />
      {!collapsed && (
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold text-white">{APP_SHORT_NAME}</p>
          <p className="truncate text-[11px] text-accent-300/90">Tender & Contract Mgmt</p>
        </div>
      )}
    </div>
  );
}

export function Sidebar({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
}: {
  collapsed: boolean;
  onToggleCollapsed: () => void;
  mobileOpen: boolean;
  onCloseMobile: () => void;
}) {
  const { can } = useAuth();
  const items = NAV_ITEMS.filter((n) => can(n.permission));
  const groups = (Object.keys(NAV_GROUP_LABELS) as NavItem['group'][])
    .map((g) => ({ g, items: items.filter((i) => i.group === g) }))
    .filter((x) => x.items.length);

  const nav = (isMobile: boolean) => (
    <nav className="flex-1 space-y-5 overflow-y-auto px-2 py-4 scrollbar-thin" aria-label="Main navigation">
      {groups.map(({ g, items }) => (
        <div key={g}>
          {(!collapsed || isMobile) && (
            <p className="mb-1.5 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-brand-300/80">{NAV_GROUP_LABELS[g]}</p>
          )}
          <ul className="space-y-0.5">
            {items.map((item) => (
              <li key={item.key}>
                <NavLink
                  to={item.path}
                  onClick={isMobile ? onCloseMobile : undefined}
                  title={collapsed && !isMobile ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      'group flex items-center gap-3 rounded-md px-2.5 py-2 text-sm font-medium transition-colors',
                      isActive ? 'bg-accent-400 text-brand-950 font-semibold' : 'text-brand-100/85 hover:bg-white/6 hover:text-white',
                      collapsed && !isMobile && 'justify-center px-0',
                    )
                  }
                >
                  <item.icon className="size-[18px] shrink-0" aria-hidden="true" />
                  {(!collapsed || isMobile) && <span className="truncate">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );

  return (
    <>
      {/* Desktop */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col bg-brand-950 transition-[width] duration-200 lg:flex',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        <div className={cn('flex h-14 items-center border-b border-white/10', collapsed ? 'justify-center' : 'px-4')}>
          <Logo collapsed={collapsed} />
        </div>
        {nav(false)}
        <button
          onClick={onToggleCollapsed}
          className="flex h-11 items-center justify-center gap-2 border-t border-white/10 text-xs text-brand-200 hover:bg-white/5 hover:text-white"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <LuPanelLeftOpen className="size-4" /> : <><LuPanelLeftClose className="size-4" /> Collapse</>}
        </button>
      </aside>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true">
          <div className="absolute inset-0 bg-slate-900/50" onClick={onCloseMobile} />
          <aside className="absolute inset-y-0 left-0 flex w-72 max-w-[85vw] flex-col bg-brand-950 shadow-xl">
            <div className="flex h-14 items-center justify-between border-b border-white/10 px-4">
              <Logo collapsed={false} />
              <button onClick={onCloseMobile} className="rounded p-1 text-brand-200 hover:text-white" aria-label="Close menu">
                <LuX className="size-5" />
              </button>
            </div>
            {nav(true)}
          </aside>
        </div>
      )}
    </>
  );
}
